import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Alert,
} from '@mui/material';
import { IDevice } from '../DevicesManagementPage';
import { ICompany, getCompanies } from '../services';

// Interface for company selections with IDs
interface CompanySelections {
  primary_company_id: number | null;
  secondary_company_id: number | null;
  tertiary_company_id: number | null;
}

// Company data will be fetched from the API

interface EditDeviceFormProps {
  device: IDevice | null;
  open: boolean;
  onClose: () => void;
  onSave: (updatedDevice: IDevice) => Promise<void>;
}

const EditDeviceForm: React.FC<EditDeviceFormProps> = ({ device, open, onClose, onSave }) => {
  const [editedDevice, setEditedDevice] = useState<IDevice | null>(null);
  const [companySelections, setCompanySelections] = useState<CompanySelections>({
    primary_company_id: null,
    secondary_company_id: null,
    tertiary_company_id: null,
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);

  // Initialize form with device data when it changes
  useEffect(() => {
    if (device) {
      setEditedDevice({ ...device }); // Find matching company IDs based on company names in the device object
      if (companies.length > 0) {
        const primaryCompanyId = device.primary_company
          ? companies.find((c) => c.name === device.primary_company)?.edgelink_company_id || null
          : null;

        const secondaryCompanyId = device.secondary_company
          ? companies.find((c) => c.name === device.secondary_company)?.edgelink_company_id || null
          : null;

        const tertiaryCompanyId = device.tertiary_company
          ? companies.find((c) => c.name === device.tertiary_company)?.edgelink_company_id || null
          : null;

        // Set company selections with mapped IDs
        setCompanySelections({
          primary_company_id: primaryCompanyId,
          secondary_company_id: secondaryCompanyId,
          tertiary_company_id: tertiaryCompanyId,
        });
      }
    }
  }, [device, companies]);

  // Fetch companies from API
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const companiesData = await getCompanies();
        setCompanies(companiesData);
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError('Failed to load company data.');
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  // Handle company selection change
  const handleCompanyChange =
    (field: 'primary_company_id' | 'secondary_company_id' | 'tertiary_company_id') =>
    (event: SelectChangeEvent<string>) => {
      const { value } = event.target;
      const numValue = value === '' ? null : parseInt(value, 10);

      // Update displayed company name in editedDevice for UI purposes
      if (editedDevice) {
        const displayField = field.replace('_id', '') as 'primary_company' | 'secondary_company' | 'tertiary_company';
        const selectedCompany = numValue ? companies.find((c) => c.edgelink_company_id === numValue) : null;

        const getDisplayValue = () => {
          if (selectedCompany) {
            return selectedCompany.name;
          }
          return numValue === null ? null : '';
        };

        setEditedDevice({
          ...editedDevice,
          [displayField]: getDisplayValue(),
        });
      }

      // Update company ID selections for API
      setCompanySelections((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    };

  // Open confirmation dialog before saving
  const handleApply = () => {
    setConfirmDialogOpen(true);
  };

  // Close confirmation dialog without saving
  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
  };

  // Close main dialog
  const handleCloseDialog = () => {
    setError(null);
    onClose();
  };

  // Save changes after confirmation
  const handleConfirm = async () => {
    if (!editedDevice) return;
    try {
      setLoading(true);
      setError(null);

      // Create an object with only the company IDs that have changed
      const updateData: Record<string, number | null> = {};

      // Only include fields that have values (changed fields)
      if (companySelections.primary_company_id !== null) {
        updateData.primary_company_id = companySelections.primary_company_id;
      }
      if (companySelections.secondary_company_id !== null) {
        updateData.secondary_company_id = companySelections.secondary_company_id;
      }
      if (companySelections.tertiary_company_id !== null) {
        updateData.tertiary_company_id = companySelections.tertiary_company_id;
      }

      // Only close the dialog if the API call is successful
      await onSave({
        ...editedDevice,
        companySelections: updateData,
      } as unknown as IDevice);

      // If we reach here, it means the API call was successful
      setConfirmDialogOpen(false);
      handleCloseDialog();
    } catch (err) {
      setError('Failed to update device. Please try again.');
      console.error('Error updating device:', err);
      // Do NOT close the confirmation dialog when there's an error
      // so the user can see the error message and try again
    } finally {
      setLoading(false);
    }
  };

  // If no device is provided, don't render anything
  if (!device || !editedDevice) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Device {device.serial}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Device Information
            </Typography>
            <Stack spacing={2} mt={2}>
              <TextField label="Serial" value={device.serial} fullWidth disabled size="small" />
              <TextField label="Location" value={device.location} fullWidth disabled size="small" />
              <FormControl fullWidth size="small">
                <InputLabel id="primary-company-label">Primary Company</InputLabel>
                <Select<string>
                  labelId="primary-company-label"
                  value={companySelections.primary_company_id?.toString() || ''}
                  onChange={handleCompanyChange('primary_company_id')}
                  label="Primary Company"
                  disabled={loadingCompanies}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {loadingCompanies ? (
                    <MenuItem disabled>Loading companies...</MenuItem>
                  ) : (
                    companies.map((company) => (
                      <MenuItem key={company.edgelink_company_id} value={company.edgelink_company_id.toString()}>
                        {company.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="secondary-company-label">Secondary Company</InputLabel>
                <Select<string>
                  labelId="secondary-company-label"
                  value={companySelections.secondary_company_id?.toString() || ''}
                  onChange={handleCompanyChange('secondary_company_id')}
                  label="Secondary Company"
                  disabled={loadingCompanies}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {loadingCompanies ? (
                    <MenuItem disabled>Loading companies...</MenuItem>
                  ) : (
                    companies.map((company) => (
                      <MenuItem key={company.edgelink_company_id} value={company.edgelink_company_id.toString()}>
                        {company.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="tertiary-company-label">Tertiary Company</InputLabel>
                <Select<string>
                  labelId="tertiary-company-label"
                  value={companySelections.tertiary_company_id?.toString() || ''}
                  onChange={handleCompanyChange('tertiary_company_id')}
                  label="Tertiary Company"
                  disabled={loadingCompanies}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {loadingCompanies ? (
                    <MenuItem disabled>Loading companies...</MenuItem>
                  ) : (
                    companies.map((company) => (
                      <MenuItem key={company.edgelink_company_id} value={company.edgelink_company_id.toString()}>
                        {company.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Stack>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleApply} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Apply Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to update the company information for device {device.serial}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}>Cancel</Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" autoFocus disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditDeviceForm;
